/*
 * ---------------------------------------------------------------------------
 * zap-article-a11y.js
 * Zenyth accessibility repairs for AUTHOR-SUPPLIED article and policy HTML.
 *
 * WHY THIS FILE EXISTS
 * Blog articles and store policies are authored in the Shopify admin, so their
 * inner HTML is not in the theme and cannot be fixed in Liquid. The 2026-07-28
 * audit of shop.zenythgroup.com recorded defects that live entirely inside that
 * content (see clients/shop_zenythgroup/audits/_MASTER-REPORT.md). This script
 * repairs the ones that can be repaired deterministically from outside, and
 * deliberately leaves alone the ones that would require rewriting the author's
 * document structure.
 *
 * WHAT IT REPAIRS
 *   R1  2.1.1 Keyboard, paired with audit fix D1 (1.4.10 Reflow)
 *       zap-styles.css gives an overflowing <pre> code block its own horizontal
 *       scroll container so the PAGE never scrolls sideways at 320px. A scroll
 *       container is only operable by keyboard if it can receive focus, so any
 *       <pre> that actually overflows gets tabindex="0", role="region" and a
 *       name. Blocks that fit are left untouched: adding a tab stop that scrolls
 *       nothing is itself a defect (that is exactly Seal's bug, see
 *       zap-seal-a11y.js R3). Re-evaluated on resize, because a block that fits
 *       at desktop width overflows at 320px.
 *
 *   R2  1.3.1 Info and Relationships (audit fix C7)
 *       Article list items authored with role="presentation" lose their
 *       list-item semantics, which also strips the surrounding list. The role is
 *       removed so the native semantics come back.
 *
 *   R5  1.3.1 Info and Relationships (audit fix C7, the nesting half)
 *       A sub-list authored as a DIRECT CHILD of its parent list (a sibling of
 *       the <li> items) is invalid, and assistive technology reports it as a
 *       separate list rather than as a child of its item. It is appended into the
 *       preceding <li>, the item it visually belongs to.
 *       This is the ONE repair in this file that moves a node. It is justified
 *       here and nowhere else: the target is static authored prose with no
 *       JavaScript attached, reading order and content are unchanged, and the
 *       transformation is deterministic. When there is no preceding <li> to adopt
 *       the list, nothing is done, because then the correct parent is a judgement
 *       call that belongs to the content owner.
 *
 *   R4  1.3.1 Info and Relationships (audit fixes C3 heading half, and C5)
 *       Headings inside authored rich text that skip a level: store policy
 *       sections arrive as h4 straight under the page h1, and product
 *       descriptions mix h2 with h4. Verified that these strings live in NO
 *       template JSON and no locale, so they come from the resource itself
 *       (policy body, product.description) and cannot be fixed in Liquid.
 *       The heading is REBUILT with the correct semantic tag (a real h2..h6):
 *       attributes copied, children moved, original replaced. House rule from
 *       Alan, 2026-07-30, use semantic tags, never role="heading" + aria-level.
 *       Rendered typography is measured before the swap and pinned inline after
 *       it if the swap changed it, so authored copy looks identical. The starting
 *       level is DERIVED from the nearest heading preceding the container, and
 *       the distinct levels inside are mapped onto a contiguous run from there,
 *       so nesting survives.
 *
 *   R3  1.3.1 Info and Relationships (audit fix C3, table half)
 *       Policy data tables put their column titles in first-row <th> cells with
 *       no scope, so the header-to-data relationship is not programmatic. Any
 *       first-row <th> inside a policy table gets scope="col", and a first-column
 *       <th> in a body row gets scope="row". Only ever ADDS a missing scope; an
 *       author-provided scope is never overwritten.
 *       NOT repaired here: the policy's heading sequence, which is a content edit.
 *
 * DESIGN RULES (same contract as zap-seal-a11y.js)
 *   Idempotent (marks what it touched), debounced, never throws, no node is
 *   unwrapped, moved, reordered or deleted, and it does nothing on pages that
 *   have no article or policy body.
 *
 * SCOPE / LIMITS
 *   This does not make author-supplied content conformant. It closes the
 *   mechanical gaps. The heading sequence, the list nesting, and any missing
 *   image alt text remain content-owner work (image alt is handled by the
 *   Zenyth Alt Text Tool pipeline, not here).
 *
 * Zenyth changelog reference: shop_zenythgroup CHANGELOG 2026-07-30, audit fixes
 * D1 (keyboard half), C7 (partial), C3 (partial).
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var FLAG = 'data-znt-article-a11y';
  var DEBOUNCE_MS = 150;
  var BODY_SEL = '.zap-article-body, .rte, .shopify-policy__body, .shopify-policy__container';

  function bodies() {
    return document.querySelectorAll(BODY_SEL);
  }

  /* R1: make an overflowing code block keyboard-scrollable, and only then. */
  function repairScrollableCode(root, stats) {
    var pres = root.querySelectorAll('pre');
    for (var i = 0; i < pres.length; i++) {
      var pre = pres[i];
      // A 1px tolerance avoids sub-pixel false positives.
      var overflows = pre.scrollWidth > pre.clientWidth + 1;
      var marked = pre.getAttribute(FLAG) === 'scroll';

      if (overflows && !marked) {
        if (!pre.hasAttribute('tabindex')) pre.setAttribute('tabindex', '0');
        if (!pre.hasAttribute('role')) pre.setAttribute('role', 'region');
        if (!pre.hasAttribute('aria-label') && !pre.hasAttribute('aria-labelledby')) {
          var lang = (pre.className.match(/language-([a-z0-9]+)/i) || [])[1];
          pre.setAttribute('aria-label', lang ? lang.toUpperCase() + ' code sample, scrollable' : 'Code sample, scrollable');
        }
        pre.setAttribute(FLAG, 'scroll');
        stats.code++;
      } else if (!overflows && marked) {
        // It fits now (viewport grew). Remove the tab stop we added so we never
        // leave a focus stop that scrolls nothing.
        pre.removeAttribute('tabindex');
        if (pre.getAttribute('role') === 'region') pre.removeAttribute('role');
        if (/code sample, scrollable$/i.test(pre.getAttribute('aria-label') || '')) pre.removeAttribute('aria-label');
        pre.removeAttribute(FLAG);
        stats.codeReverted++;
      }
    }
  }

  /* R2: restore list-item semantics stripped by role="presentation". */
  function repairListSemantics(root, stats) {
    var items = root.querySelectorAll('li[role="presentation"], li[role="none"]');
    for (var i = 0; i < items.length; i++) {
      items[i].removeAttribute('role');
      items[i].setAttribute(FLAG, 'list');
      stats.list++;
    }
  }

  /* R3: add the missing scope on policy table headers. Never overwrite an author's. */
  function repairTableScopes(root, stats) {
    var tables = root.querySelectorAll('table');
    for (var t = 0; t < tables.length; t++) {
      var table = tables[t];
      if (table.getAttribute(FLAG) === 'scope') continue;
      var rows = table.rows;
      if (!rows || !rows.length) continue;
      var touched = false;

      // First row: any th in it names a column.
      var firstCells = rows[0].cells;
      for (var c = 0; c < firstCells.length; c++) {
        var cell = firstCells[c];
        if (cell.tagName !== 'TH') continue;
        if (cell.getAttribute('scope')) continue;
        if (!cell.textContent.trim()) continue; // an empty th names nothing, leave it
        cell.setAttribute('scope', 'col');
        touched = true;
        stats.scopeCol++;
      }

      // Body rows: a th in the first column names its row.
      for (var r = 1; r < rows.length; r++) {
        var first = rows[r].cells[0];
        if (!first || first.tagName !== 'TH') continue;
        if (first.getAttribute('scope')) continue;
        if (!first.textContent.trim()) continue;
        first.setAttribute('scope', 'row');
        touched = true;
        stats.scopeRow++;
      }

      if (touched) table.setAttribute(FLAG, 'scope');
    }
  }

  /* R5: repair a list nested as a DIRECT CHILD of its parent list (audit fix C7, WCAG 1.3.1).
     Authored HTML sometimes places a sub-list as a sibling of the <li> items instead of inside the
     preceding one. That is invalid, and assistive technology reports the sub-list as a separate list
     rather than as a child of its item, so the outline the reader hears does not match the visual one.

     This is the ONE place this file moves a node, and the reason it is safe here: the target is static
     authored prose with no JavaScript attached, the sub-list is appended to the item it visually belongs
     to, so reading order and content are identical, and the transformation is deterministic. It is NOT
     applied when there is no preceding <li> to adopt the list, because then the correct parent is a
     judgement call and that belongs to the content owner. */
  function repairListNesting(root, stats) {
    var lists = root.querySelectorAll('ul, ol');
    for (var i = 0; i < lists.length; i++) {
      var list = lists[i];
      if (list.getAttribute(FLAG) === 'nesting') continue;
      var strays = [];
      for (var c = 0; c < list.children.length; c++) {
        var child = list.children[c];
        if (child.tagName === 'UL' || child.tagName === 'OL') strays.push(child);
      }
      if (!strays.length) continue;
      for (var s = 0; s < strays.length; s++) {
        var stray = strays[s];
        // Find the nearest preceding sibling that is a real list item.
        var prev = stray.previousElementSibling;
        while (prev && prev.tagName !== 'LI') prev = prev.previousElementSibling;
        if (!prev) continue; // nothing to adopt it, leave for the content owner
        prev.appendChild(stray);
        stray.setAttribute(FLAG, 'nesting');
        stats.nesting++;
      }
      list.setAttribute(FLAG, 'nesting');
    }
  }

  /* R4: re-level headings inside AUTHORED rich text using REAL SEMANTIC TAGS (WCAG 1.3.1).
     Two real cases on this store, same defect shape:
       - store policies: section titles arrive as h4 directly under the page h1 (audit fix C3)
       - product descriptions: the body mixes h2 with h4, so it reads h2 then h4 (audit fix C5)
     Both bodies are authored in the Shopify admin. Verified: the strings exist in NO template JSON and
     no locale, so they genuinely come from the resource (policy body / product.description).

     HOUSE RULE (Alan, 2026-07-30): use semantic tags, NOT role="heading" + aria-level. An earlier
     version of this function set aria-level and left the wrong tag in place; that reads correctly to a
     screen reader but leaves the document's real outline wrong for everything else that consumes it
     (the DOM, the HTML outline, other tooling, anyone reading the source). So the element is REBUILT
     with the correct tag: a new h2..h6 is created, every attribute is copied, every child node is moved
     over, and the original is replaced.

     Preserving the design is part of the job. Some theme CSS targets heading TAGS directly, so swapping
     h4 for h2 could silently restyle authored copy. The typography that matters is therefore measured
     BEFORE the swap and, if the swap changed it, pinned inline afterwards. In practice that means the
     copy looks identical and only the semantics change.

     The level each container starts at is DERIVED, not assumed: it is one below the nearest heading that
     precedes the container in the document, clamped to 2..6. The distinct levels inside are then mapped
     in order onto a contiguous run from there, so relative nesting survives instead of being flattened.
     For both cases here the nearest preceding heading is the page or product h1, so the run starts at 2:
     a policy's uniform h4 becomes h2, and a description's h2/h4 pair becomes h2/h3. */
  var PINNED_STYLES = ['fontSize', 'fontWeight', 'lineHeight', 'fontFamily', 'letterSpacing', 'textTransform', 'color', 'marginTop', 'marginBottom'];

  function nearestPrecedingHeadingLevel(container) {
    var all = Array.from(document.querySelectorAll('h1, h2, h3, h4, h5, h6'));
    var level = 1; // default: assume a page h1 sits above the content
    for (var i = 0; i < all.length; i++) {
      var h = all[i];
      if (container.contains(h)) break; // reached the container itself, stop
      if (!h.textContent.trim()) continue;
      var l = parseInt(h.tagName.slice(1), 10);
      if (!isNaN(l)) level = l;
    }
    return level;
  }

  /* Rebuild a heading with a different tag, keeping attributes, children and rendered typography. */
  function retagHeading(el, targetLevel) {
    var before = {};
    var cs = window.getComputedStyle(el);
    for (var s = 0; s < PINNED_STYLES.length; s++) before[PINNED_STYLES[s]] = cs[PINNED_STYLES[s]];

    var next = document.createElement('h' + targetLevel);
    for (var a = 0; a < el.attributes.length; a++) {
      next.setAttribute(el.attributes[a].name, el.attributes[a].value);
    }
    while (el.firstChild) next.appendChild(el.firstChild);
    next.setAttribute(FLAG, 'retag');
    next.setAttribute('data-znt-original-tag', el.tagName.toLowerCase());
    el.parentNode.replaceChild(next, el);

    // Keep the design identical: pin only the properties the tag swap actually changed.
    var after = window.getComputedStyle(next);
    for (var p = 0; p < PINNED_STYLES.length; p++) {
      var prop = PINNED_STYLES[p];
      if (after[prop] !== before[prop]) next.style[prop] = before[prop];
    }
    return next;
  }

  function repairAuthoredHeadingLevels(stats) {
    /* Scope to the AUTHORED body only. Using an outer wrapper (e.g. .shopify-policy__container) also
       sweeps in the template's own page h1, which made the "does this already start low enough?" test
       see a level 1 and bail out silently with nothing done. */
    var containers = document.querySelectorAll(
      '.shopify-policy__body, .product-details rte-formatter, .product-details .text-block'
    );

    for (var ci = 0; ci < containers.length; ci++) {
      var box = containers[ci];
      if (box.getAttribute(FLAG) === 'levels') continue;

      var hs = Array.from(box.querySelectorAll('h1, h2, h3, h4, h5, h6')).filter(function (h) {
        return h.textContent.trim();
      });
      if (!hs.length) continue;

      var startLevel = Math.min(6, Math.max(2, nearestPrecedingHeadingLevel(box) + 1));

      var levels = hs.map(function (h) { return parseInt(h.tagName.slice(1), 10); });
      var distinct = levels.filter(function (v, i, a) { return a.indexOf(v) === i; }).sort(function (a, b) { return a - b; });

      var map = {};
      for (var d = 0; d < distinct.length; d++) map[distinct[d]] = Math.min(6, startLevel + d);

      // If every level already sits where it should, leave the DOM completely alone.
      var needsWork = false;
      for (var k = 0; k < distinct.length; k++) if (map[distinct[k]] !== distinct[k]) needsWork = true;
      if (!needsWork) { box.setAttribute(FLAG, 'levels'); continue; }

      for (var i = 0; i < hs.length; i++) {
        var h = hs[i];
        var current = parseInt(h.tagName.slice(1), 10);
        var target = map[current];
        if (!target || target === current) continue;
        retagHeading(h, target); // real <h2>..<h6>, never role="heading" + aria-level
        stats.levels++;
      }
      box.setAttribute(FLAG, 'levels');
    }
  }

  function runPass() {
    var stats = { code: 0, codeReverted: 0, list: 0, nesting: 0, scopeCol: 0, scopeRow: 0, levels: 0 };
    var roots = bodies();
    for (var i = 0; i < roots.length; i++) {
      try {
        repairScrollableCode(roots[i], stats);
        repairListSemantics(roots[i], stats);
        repairListNesting(roots[i], stats);
        repairTableScopes(roots[i], stats);
      } catch (err) {
        /* one bad node must never break the page */
      }
    }
    try {
      repairAuthoredHeadingLevels(stats);
    } catch (err) {
      /* never break the page */
    }
    return stats;
  }

  var timer = null;
  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () {
      timer = null;
      runPass();
    }, DEBOUNCE_MS);
  }

  function boot() {
    if (!bodies().length) return; // no article or policy body on this page
    runPass();
    // Overflow depends on viewport width, so re-evaluate on resize. No
    // MutationObserver: this content is server-rendered and does not change
    // after load, so an always-on observer would cost without buying anything.
    window.addEventListener('resize', schedule);
    // Fonts can load after first paint and change whether a line overflows.
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(schedule);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
